import { useEffect, useRef, useState } from 'react';

export interface SelectionSubmitResult {
  failedKeys: readonly string[];
  error?: string;
}

/** Keep failed selections for retry and prevent dismissal or duplicate writes during submission. */
export function useSelectionSubmission<T>({
  selected,
  getKey,
  disabled = false,
  onSubmit,
  onSettled,
  onClose,
  partialFailureMessage,
  failureMessage,
}: {
  selected: T[];
  getKey: (item: T) => string;
  disabled?: boolean;
  onSubmit: (items: T[]) => Promise<SelectionSubmitResult>;
  onSettled: (failed: T[], succeeded: T[]) => void;
  onClose: () => void;
  partialFailureMessage: string;
  failureMessage: string;
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  const pending = useRef(false);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  const close = () => {
    if (!pending.current) onClose();
  };
  const submit = async () => {
    if (disabled || pending.current || !selected.length) return;
    pending.current = true;
    setIsSaving(true);
    setError(undefined);
    try {
      const result = await onSubmit(selected);
      if (!active.current) return;
      const failedKeys = new Set(result.failedKeys);
      const failed = selected.filter((item) => failedKeys.has(getKey(item)));
      const succeeded = selected.filter((item) => !failedKeys.has(getKey(item)));
      onSettled(failed, succeeded);
      if (!failed.length) onClose();
      else setError(result.error ?? partialFailureMessage);
    } catch {
      if (active.current) setError(failureMessage);
    } finally {
      pending.current = false;
      if (active.current) setIsSaving(false);
    }
  };

  return { isSaving, error, submit, close };
}
